import { clearElement, createElement, requireElement } from "../ui/dom";
import {
  STATE_CATEGORY_LABELS,
  STATE_SCENARIOS,
  findStateScenario,
} from "./scenarios";
import { simulateStateScenario } from "./simulator";
import { STATE_APP_TEMPLATE } from "./template";
import type {
  ArticleState,
  StateScenario,
  StateSimulationResult,
  TimelineStep,
} from "./types";

const DEFAULT_STATE_SCENARIO = "validator-scope";

function setText(selector: string, value: string): void {
  requireElement<HTMLElement>(selector).textContent = value;
}

function renderState(prefix: "before" | "after", state: ArticleState): void {
  setText(`#state-${prefix}-revision`, `Revision ${state.revision}`);
  setText(`#state-${prefix}-sbr`, state.sbrEtag);
  setText(`#state-${prefix}-projection`, state.projectionEtag);
  setText(`#state-${prefix}-semantic`, state.semanticEtag);
}

function renderDraftLinks(scenario: StateScenario): void {
  const container = requireElement<HTMLElement>("#state-draft-links");
  clearElement(container);
  for (const reference of scenario.references) {
    const link = createElement("a", "scenario-draft-link", reference.label);
    link.href = reference.href;
    link.target = "_blank";
    link.rel = "noreferrer";
    container.append(link);
  }
}

function renderTimelineStep(step: TimelineStep): HTMLElement {
  const article = createElement("article", "timeline-step");
  article.dataset.tone = step.tone;

  const number = createElement("span", "timeline-step__number", step.number);
  number.setAttribute("aria-hidden", "true");
  const copy = createElement("div", "timeline-step__copy");
  copy.append(
    createElement("p", "eyebrow", step.eyebrow),
    createElement("h4", undefined, step.title),
    createElement("p", "timeline-step__detail", step.detail),
  );
  const wire = createElement("pre", "timeline-step__wire");
  wire.textContent = step.wire;
  article.append(number, copy, wire);
  return article;
}

function renderResult(result: StateSimulationResult): void {
  renderState("before", result.stateBefore);
  renderState("after", result.stateAfter);

  const outcome = requireElement<HTMLElement>("#state-outcome");
  outcome.dataset.outcome = result.outcome;
  setText("#state-mode-label", result.modeLabel);
  setText("#state-outcome-label", result.outcomeLabel);
  setText("#state-outcome-explanation", result.explanation);
  const commitParts = [
    `${result.clientCommitCount} client commit${result.clientCommitCount === 1 ? "" : "s"}`,
  ];
  if (result.externalCommitCount > 0) {
    commitParts.push(
      `${result.externalCommitCount} intervening commit${result.externalCommitCount === 1 ? "" : "s"}`,
    );
  }
  setText(
    "#state-outcome-counts",
    `${result.requestCount} local exchange${result.requestCount === 1 ? "" : "s"} · ${commitParts.join(" · ")}`,
  );

  const timeline = requireElement<HTMLElement>("#state-timeline");
  clearElement(timeline);
  for (const step of result.steps) {
    timeline.append(renderTimelineStep(step));
  }
}

function renderScenario(scenario: StateScenario): void {
  setText("#state-scenario-category", STATE_CATEGORY_LABELS[scenario.category]);
  setText("#state-scenario-title", scenario.title);
  setText("#state-scenario-summary", scenario.summary);
  setText("#state-scenario-rule", scenario.rule);
  renderDraftLinks(scenario);
  renderResult(simulateStateScenario(scenario));
}

function populateScenarioSelect(select: HTMLSelectElement): void {
  clearElement(select);
  for (const category of Object.keys(STATE_CATEGORY_LABELS) as Array<
    keyof typeof STATE_CATEGORY_LABELS
  >) {
    const group = document.createElement("optgroup");
    group.label = STATE_CATEGORY_LABELS[category];
    for (const scenario of STATE_SCENARIOS.filter(
      (candidate) => candidate.category === category,
    )) {
      const option = document.createElement("option");
      option.value = scenario.id;
      option.textContent = scenario.title;
      group.append(option);
    }
    select.append(group);
  }
}

export function mountStateDemo(): void {
  const app = requireElement<HTMLDivElement>("#app");
  app.innerHTML = STATE_APP_TEMPLATE;

  const select = requireElement<HTMLSelectElement>("#state-scenario-select");
  populateScenarioSelect(select);
  setText(
    "#state-scenario-count",
    `${STATE_SCENARIOS.length} deterministic local scenarios · selection evaluates immediately`,
  );
  setText(
    "#state-evidence-count",
    `${STATE_SCENARIOS.length} deterministic local cases`,
  );

  const initial = findStateScenario(DEFAULT_STATE_SCENARIO);
  if (initial === undefined) {
    throw new Error(`Default state scenario is missing: ${DEFAULT_STATE_SCENARIO}`);
  }
  select.value = initial.id;
  renderScenario(initial);

  select.addEventListener("change", () => {
    const scenario = findStateScenario(select.value);
    if (scenario !== undefined) {
      renderScenario(scenario);
    }
  });
}
